import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { ShieldCheck, Loader2, Camera, Clock, Trash2, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { t, tf } from '../lib/i18n';
import { profileAPI } from '../lib/api';

const POINTS = [
  { key: 'consentPoint1', icon: Camera },
  { key: 'consentPoint2', icon: Clock },
  { key: 'consentPoint3', icon: Trash2 },
  { key: 'consentPoint4', icon: EyeOff },
];

/**
 * Explicit photo-usage consent shown before the first photo analysis.
 * Records the consent server-side (the API refuses photo endpoints without it).
 */
export const PhotoConsentDialog = ({ open, onOpenChange, language, retentionDays = 90, onAccepted }) => {
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleAccept = async () => {
    if (!checked || saving) return;
    setSaving(true);
    try {
      await profileAPI.consent(true);
      toast.success(t('consentSaved', language));
      onOpenChange(false);
      onAccepted?.();
    } catch (err) {
      toast.error(t('consentError', language));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[var(--lux-panel)] border-white/10 max-w-lg" data-testid="photo-consent-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[var(--lux-gold)]" /> {t('consentTitle', language)}
          </DialogTitle>
          <DialogDescription className="text-white/50">{t('consentIntro', language)}</DialogDescription>
        </DialogHeader>

        <ul className="space-y-3 my-2">
          {POINTS.map(({ key, icon: Icon }) => (
            <li key={key} className="flex items-start gap-3 text-sm text-white/80">
              <span className="p-1.5 rounded-md bg-lux-gold/10 mt-0.5"><Icon className="w-3.5 h-3.5 text-[var(--lux-gold)]" /></span>
              <span>{tf(key, language, { days: retentionDays })}</span>
            </li>
          ))}
        </ul>

        <div className="flex items-start gap-3 p-3 rounded-lg bg-black/20 border border-white/10">
          <Checkbox
            id="photo-consent-checkbox"
            data-testid="photo-consent-checkbox"
            checked={checked}
            onCheckedChange={(v) => setChecked(Boolean(v))}
            className="mt-0.5 border-white/30 data-[state=checked]:bg-[var(--lux-gold)] data-[state=checked]:border-[var(--lux-gold)] data-[state=checked]:text-[var(--lux-ink)]"
          />
          <Label htmlFor="photo-consent-checkbox" className="text-sm text-white/80 leading-snug cursor-pointer">
            {t('consentCheckbox', language)}
          </Label>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="bg-[var(--lux-panel)] border-white/10 text-white" data-testid="photo-consent-cancel">
            {t('consentCancel', language)}
          </Button>
          <Button onClick={handleAccept} disabled={!checked || saving} className="bg-[var(--lux-gold)] text-[var(--lux-ink)] hover:bg-[var(--lux-gold-2)]" data-testid="photo-consent-accept">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
            {t('consentAccept', language)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
